/** Walk up from the editor root to the element that actually scrolls. */
export const findScrollParent = (el) => {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const style = getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(style.overflowY)) return node;
    node = node.parentElement;
  }
  return null;
};

/** Line-height (px) of the block's editable text. */
export const textLineHeight = (wrapper) => {
  const editable = wrapper.querySelector('[contenteditable], .doc-editable');
  if (!editable) return 24;
  const lh = parseFloat(getComputedStyle(editable).lineHeight);
  return Number.isFinite(lh) && lh > 0 ? lh : 24;
};

/**
 * Canvas-ready colour for a variable pill — matches the on-page text tint
 * (resolved colour / empty mix / unresolved red), including element opacity.
 */
export const pillMinimapColor = (pill) => {
  const cs = getComputedStyle(pill);
  const opacity = Number.parseFloat(cs.opacity);
  const elemA = Number.isFinite(opacity) ? opacity : 1;

  const colorStr = cs.color || '';
  const m = String(colorStr).match(/rgba?\(([^)]+)\)/i);
  if (m) {
    const body = m[1].trim();
    let r;
    let g;
    let b;
    let a = 1;
    if (body.includes(',')) {
      const parts = body.split(',').map((v) => v.trim());
      r = parseFloat(parts[0]);
      g = parseFloat(parts[1]);
      b = parseFloat(parts[2]);
      if (parts[3] != null) {
        const raw = parts[3].trim();
        a = raw.endsWith('%') ? parseFloat(raw) / 100 : parseFloat(raw);
      }
    } else {
      const [rgbPart, alphaPart] = body.split('/').map((s) => s.trim());
      const channels = rgbPart.split(/\s+/).map((v) => parseFloat(v));
      if (channels.length >= 3) {
        [r, g, b] = channels;
        if (alphaPart != null) {
          a = alphaPart.endsWith('%') ? parseFloat(alphaPart) / 100 : parseFloat(alphaPart);
        }
      }
    }
    if (![r, g, b].some((v) => Number.isNaN(v))) {
      // Hairlines need a touch more alpha than full-size pills to stay readable
      const alpha = Math.max(0.4, Math.min(1, a * elemA * 1.2));
      return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${alpha})`;
    }
  }

  const raw = cs.getPropertyValue('--var-color').trim();
  if (raw) return raw;
  return 'rgba(255, 107, 107, 0.7)';
};

/**
 * Group client rects into lines by vertical position (half a line tolerance).
 * Returns [{ top, left, right }, …] sorted top→bottom.
 */
export const groupRectsIntoLines = (rects, lineHeight) => {
  const lines = [];
  rects
    .slice()
    .sort((a, b) => a.top - b.top)
    .forEach((r) => {
      const line = lines.find((l) => Math.abs(l.top - r.top) < lineHeight / 2);
      if (line) {
        line.left = Math.min(line.left, r.left);
        line.right = Math.max(line.right, r.right);
      } else {
        lines.push({ top: r.top, left: r.left, right: r.right });
      }
    });
  return lines;
};

/**
 * Variable pill segments for each text line, as fractions of `widthPx`.
 * `lines` is the [{ top, … }] array from groupRectsIntoLines (or equivalent).
 * Returns Array<Array<{ start, width, color }>> aligned with `lines`.
 */
export const variableSegmentsForLines = (host, lines, widthPx, originLeft, lineHeight) => {
  const empty = () => lines.map(() => []);
  if (!host || !lines.length || widthPx <= 0) return empty();
  const pills = host.querySelectorAll('[data-doc-variable], [data-doc-math]');
  if (pills.length === 0) return empty();

  const result = lines.map(() => []);
  pills.forEach((pill) => {
    const color = pillMinimapColor(pill);
    Array.from(pill.getClientRects()).forEach((r) => {
      if (r.width <= 0 || r.height <= 0) return;
      let bestIdx = -1;
      let bestDist = Infinity;
      for (let i = 0; i < lines.length; i += 1) {
        const dist = Math.abs(lines[i].top - r.top);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = i;
        }
      }
      if (bestIdx < 0 || bestDist >= lineHeight) return;
      const start = Math.max(0, Math.min(0.99, (r.left - originLeft) / widthPx));
      const width = Math.max(
        1 / widthPx,
        Math.min(1 - start, r.width / widthPx)
      );
      result[bestIdx].push({ start, width, color });
    });
  });
  return result;
};

/**
 * REAL width of each rendered text line + coloured variable pill segments.
 * Range.getClientRects() gives one rect per line fragment; fragments are
 * grouped into lines by vertical position.
 *
 * Returns `{ lineFracs, lineVars }` or null.
 *   lineFracs: number[] — width fraction of each line
 *   lineVars:  Array<Array<{ start, width, color }>> — pill segments per line
 *              (start/width as fractions of the block width)
 */
export const textLineLayout = (wrapper, blockWidthPx) => {
  const editable = wrapper.querySelector('[contenteditable], .doc-editable');
  if (!editable || blockWidthPx <= 0) return null;
  try {
    const range = document.createRange();
    range.selectNodeContents(editable);
    const rects = Array.from(range.getClientRects()).filter(
      (r) => r.width > 0 && r.height > 0
    );
    range.detach?.();
    if (rects.length === 0) return null;
    const lh = textLineHeight(wrapper);
    const lines = groupRectsIntoLines(rects, lh);
    const blockLeft = wrapper.getBoundingClientRect().left;
    const lineFracs = lines.map((l) =>
      Math.max(0.06, Math.min(1, (l.right - l.left) / blockWidthPx))
    );
    const lineVars = variableSegmentsForLines(
      editable,
      lines,
      blockWidthPx,
      blockLeft,
      lh
    );
    return { lineFracs, lineVars };
  } catch {
    return null;
  }
};

/**
 * REAL width of each rendered text line, as fractions of the block's width.
 * @deprecated Prefer textLineLayout — kept for callers that only need widths.
 */
export const textLineWidthFracs = (wrapper, blockWidthPx) => {
  const layout = textLineLayout(wrapper, blockWidthPx);
  return layout ? layout.lineFracs : null;
};
